import type { TranslationKey } from '../../i18n'
import type { Lot } from '../../types/lot'

export type LotsTableScope = 'all-facilities' | 'facility' | 'production-unit' | 'processing-site'

type SortLotsForDisplayParams = {
  scope: LotsTableScope
  farmLookup: ReadonlyMap<string, string>
  productionUnitLookup: ReadonlyMap<string, string>
}

type BuildLotsTableSummaryParams = {
  lots: Lot[]
  scope: LotsTableScope
}

type LotsTableSummary = {
  key: TranslationKey
  variables: Record<string, number>
}

const compareText = (left: string, right: string): number => left.localeCompare(right)

const compareLotsByKey = (left: Lot, right: Lot): number => {
  const lotKeyCompare = compareText(left.lotKey ?? '', right.lotKey ?? '')
  if (lotKeyCompare !== 0) return lotKeyCompare
  return (right.updatedAt ?? '').localeCompare(left.updatedAt ?? '')
}

const displayNameFor = (lookup: ReadonlyMap<string, string>, value: string | undefined): string => {
  const normalizedValue = String(value ?? '').trim()
  if (!normalizedValue) return ''
  return lookup.get(normalizedValue) ?? normalizedValue
}

export const getLotsTableScope = (params: {
  selectedFarmId: string
  selectedProductionUnitId: string
  selectedProcessingSiteId: string
}): LotsTableScope => {
  if (params.selectedProductionUnitId) {
    return 'production-unit'
  }
  if (params.selectedProcessingSiteId) {
    return 'processing-site'
  }
  if (params.selectedFarmId) {
    return 'facility'
  }
  return 'all-facilities'
}

export const sortLotsForDisplay = (lots: Lot[], params: SortLotsForDisplayParams): Lot[] => (
  lots
    .slice()
    .sort((left, right) => {
      if (params.scope === 'all-facilities') {
        const facilityCompare = compareText(
          displayNameFor(params.farmLookup, left.farmId),
          displayNameFor(params.farmLookup, right.farmId)
        )
        if (facilityCompare !== 0) return facilityCompare
      }

      if (params.scope !== 'production-unit') {
        const unitCompare = compareText(
          displayNameFor(params.productionUnitLookup, left.productionUnitId),
          displayNameFor(params.productionUnitLookup, right.productionUnitId)
        )
        if (unitCompare !== 0) return unitCompare
      }

      return compareLotsByKey(left, right)
    })
)

export const buildLotsTableSummary = (params: BuildLotsTableSummaryParams): LotsTableSummary => {
  const facilityCount = new Set(params.lots.map((lot) => lot.farmId).filter((farmId) => farmId.trim().length > 0)).size
  const productionUnitCount = new Set(
    params.lots.map((lot) => lot.productionUnitId).filter((productionUnitId): productionUnitId is string => Boolean(productionUnitId?.trim().length))
  ).size

  if (params.scope === 'production-unit') {
    return {
      key: 'lots.table.scope.productionUnit',
      variables: { count: params.lots.length }
    }
  }

  if (params.scope === 'processing-site') {
    return {
      key: 'lots.table.scope.processingSite',
      variables: { count: params.lots.length }
    }
  }

  if (params.scope === 'facility') {
    return {
      key: 'lots.table.scope.facility',
      variables: {
        count: params.lots.length,
        units: productionUnitCount
      }
    }
  }

  return {
    key: 'lots.table.scope.allFacilities',
    variables: {
      count: params.lots.length,
      facilities: facilityCount,
      units: productionUnitCount
    }
  }
}

export const getLotsTableEmptyKey = (scope: LotsTableScope): TranslationKey => {
  if (scope === 'production-unit') {
    return 'lots.table.empty.productionUnit'
  }
  if (scope === 'processing-site') {
    return 'lots.table.empty.processingSite'
  }
  if (scope === 'facility') {
    return 'lots.table.empty.facility'
  }
  return 'lots.table.empty.allFacilities'
}